/* ⚠️ GENERATED from app/services/syncFanout.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/syncFanout.ts — who is owed a copy of a message before it may be swept.
//
// 🟥 A SWEEP IS NOT A MOVE. IT IS A DESTRUCTION, FOR EVERY DEVICE THAT HAS NO
//    LOCAL DATABASE. Store-and-forward deletes a message document within ~50ms
//    of this phone caching it in SQLite. That is right for two phones: the bytes
//    moved from the cloud into a durable local store. It is wrong for a browser,
//    which has no SQLite, reads the documents themselves, and may not have been
//    opened for eight hours.
//
//    This is the whole reason the web had to ask the phone for history, and the
//    whole reason the answer took "a bit" to arrive at night: by the time the
//    browser was opened, every document it had missed was already gone and the
//    only surviving copy was on a phone that had to be AWAKE to hand it over.
//    A phone is a bad server. It sleeps, it gets force-quit, and on iOS the
//    operating system will not wake it to answer a question.
//
// 🟢 SO THE PHONE LEAVES A COPY BEHIND INSTEAD OF ANSWERING QUESTIONS. Before it
//    sweeps, it writes the row into a per-device mailbox sealed to each linked
//    browser's public key. The browser drains its own mailbox whenever it opens
//    — phone asleep, phone dead, phone in a drawer. Nothing has to be awake to
//    answer a question nobody has to ask.
//
// 🟢 AND THE RULE LIVES HERE RATHER THAN AT THE CALL SITE. ChatModal already
//    carried one hand-written instance of it — `isOfficialConversation`: do not
//    sweep, because the browser reads these straight from Firestore and there is
//    no local database in a tab. That guard was correct, and it was the general
//    rule written down once, for one chat, where no other sweep could find it.

/** How long a sealed copy waits in a mailbox nobody drains. */
/* 🟥 A MAILBOX IS NOT AN ARCHIVE, AND THE TTL IS A PRIVACY CONTROL RATHER THAN
   A STORAGE ONE. Every entry is a message this account can already read, sitting
   in the cloud, for a browser that may never come back — a laptop sold, a tab
   closed for good, a device unlinked from somewhere else. Thirty days is long
   enough for a holiday and short enough that an abandoned browser stops
   accumulating a readable copy of a life. */
export const MAILBOX_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/* 🟢 …AND A DEVICE THAT HAS NOT COME BACK IN SIX WEEKS IS NOT OWED ANYTHING NEW.
   Writing to it costs a document per message for a reader that has stopped
   existing. It is not unlinked — if it ever returns it links again and asks for
   a backfill, which is exactly the one job the backfill still has. */
export const DEVICE_IDLE_MS = 45 * 24 * 60 * 60 * 1000;
export const WEB_PLATFORM = 'web';

/** Is this a browser — the only kind of device with nowhere durable to land? */
export function isWebDevice(d) {
  return String(d?.platform || '').trim().toLowerCase() === WEB_PLATFORM;
}

/**
 * Can this device be handed a sealed copy at all?
 *
 * 🟥 NO PUBLIC KEY MEANS NO COPY, AND IT IS NOT A DEGRADED CASE — it is the
 *    half-registered device CryptoService.ensureMyKeyPublished exists to heal:
 *    an entry created by the dot-path FCM-token write, carrying a token and no
 *    key. Sealing to it is impossible; treating it as owed would block every
 *    sweep on the account forever, which turns a missing key into a storage leak.
 */
export function canReceive(d, opts = {}) {
  if (!d || !d.deviceId) return false;
  /* 🟥 BOTH SPELLINGS, BECAUSE ONE CALLER USED THE OTHER ONE AND SILENTLY
     EXCLUDED NOBODY. allo-web's mirrorOut passes `selfDeviceId`; this read only
     `myDeviceId`, so the self-check never applied there — a browser was eligible
     to post to its own mailbox. Accepting both is one line; a name that has to be
     remembered across two clients is a bug waiting for the next caller. */
  const selfId = opts.myDeviceId ?? opts.selfDeviceId;
  if (selfId && d.deviceId === selfId) return false;
  if (!d.publicKey) return false;
  const now = Number(opts.now) || Date.now();
  const idleMs = Number(opts.idleMs) || DEVICE_IDLE_MS;
  const seen = Number(d.lastSeenAt) || 0;
  /* 🟢 A DEVICE WITH NO lastSeenAt IS NEW, NOT ANCIENT. It has registered a key
     and has not checked in yet; refusing it would mean a browser linked five
     minutes ago never receives anything. */
  if (seen > 0 && now - seen > idleMs) return false;
  return true;
}

/**
 * Every OTHER device on this account that can be handed a copy — phones included.
 *
 * 🟥 THIS IS THE HALF THAT DID NOT EXIST, AND ITS ABSENCE IS WHY THE PHONE NEVER
 *    KNEW. `owedDevices` answers "which linked BROWSERS must be left a copy" — it
 *    is the sweep's question, asked by a phone, and it filters to web devices by
 *    design. allo-web's mirrorOut asked that same function who to mirror TO, and
 *    a phone is not a browser: `owed` came back empty every single time, so the
 *    web wrote nothing to the phone's mailbox, runMailboxImport always drained an
 *    empty collection, and the skeletons and the "importing from the web" row had
 *    no input to react to. All of that machinery has never once had anything to do.
 *
 * 🟢 THE PLATFORM QUESTION IS NOW THE CALLER'S, NOT BAKED IN. The freshness, key
 *    and self checks stay in one place; who counts as an audience is asked
 *    explicitly, so neither client can inherit the other's meaning by accident.
 */
export function mailboxAudience(devices, opts = {}) {
  const out = [];
  const map = devices && typeof devices === 'object' ? devices : {};
  for (const deviceId of Object.keys(map)) {
    const raw = map[deviceId] || {};
    const d = {
      deviceId,
      publicKey: raw.publicKey ?? null,
      platform: raw.platform ?? null,
      name: raw.deviceName ?? raw.name ?? null,
      lastSeenAt: Number(raw.lastSeenAt ?? raw.updatedAt ?? 0) || 0
    };
    if (canReceive(d, opts)) out.push(d);
  }
  return out;
}

/** Every linked browser that must be left a copy, oldest-registered first. */
export function owedDevices(devices, opts = {}) {
  const out = [];
  const map = devices && typeof devices === 'object' ? devices : {};
  for (const deviceId of Object.keys(map)) {
    const raw = map[deviceId] || {};
    const d = {
      deviceId,
      publicKey: raw.publicKey ?? null,
      platform: raw.platform ?? null,
      name: raw.deviceName ?? raw.name ?? null,
      lastSeenAt: Number(raw.lastSeenAt ?? raw.updatedAt ?? 0) || 0
    };
    /* 🟥 BROWSERS ONLY, AND THAT TEST NOW LIVES HERE. It used to sit inside
       canReceive, which made it invisible to the caller and gave allo-web's
       mirrorOut a phone-shaped answer of "nobody" — see mailboxAudience. Moving
       it out changed nothing about THIS function's meaning, and it must not: a
       sweep is allowed to delete a message once every device that has nowhere
       durable to put it has been handed a copy, and only a browser has nowhere. */
    if (isWebDevice(d) && canReceive(d, opts)) out.push(d);
  }
  return out;
}
/**
 * May this message be deleted from the cloud yet?
 *
 * 🟢 THE REASON IS RETURNED, NOT LOGGED — the same shape `scoreOne` uses, and for
 *    the same reason: a sweep that refuses to run is invisible, and a sweep that
 *    runs when it should not is worse than invisible. Whatever decides has to be
 *    able to say which of the two it just did.
 */
export function sweepDecision(opts = {}) {
  /* 🟥 THE OFFICIAL CHAT IS NEVER SEALED AND NEVER SWEPT. The browser and the
     staff console both read those documents directly; deleting one destroys the
     ticket for whoever has to answer it. */
  if (opts.isOfficial) return {
    sweep: false,
    owed: [],
    reason: 'official'
  };
  const done = new Set((opts.mailboxed || []).map(String));
  const owed = (opts.owed || []).map(d => d.deviceId).filter(id => id && !done.has(String(id)));
  if (owed.length) return {
    sweep: false,
    owed,
    reason: 'owed'
  };
  return {
    sweep: true,
    owed: [],
    reason: 'clear'
  };
}

/** Where a browser's sealed copies wait for it. */
export function mailboxPath(uid, deviceId) {
  return ['users', String(uid), 'device_inbox', String(deviceId), 'messages'];
}

/**
 * Is this mailbox entry finished with?
 *
 * 🟥 DRAINED IS NOT THE ONLY WAY OUT, or a browser that never returns leaves a
 *    readable copy of every message it missed in the cloud for ever. Age is the
 *    other way out, and it has to be checked by whoever writes as well as by
 *    whoever reads — a reader that never comes back cannot expire its own post.
 */
export function mailboxExpired(entry, opts = {}) {
  if (!entry) return true;
  if (entry.drained === true) return true;
  const now = Number(opts.now) || Date.now();
  const ttl = Number(opts.ttlMs) || MAILBOX_TTL_MS;
  const at = Number(entry.at) || 0;
  /* 🟢 AN ENTRY WITH NO TIMESTAMP IS NOT INFINITELY OLD. A serverTimestamp reads
     back as null on the write's own local echo; treating that as expired would
     delete the copy microseconds after writing it. */
  if (!at) return false;
  return now - at > ttl;
}
